import { useLayoutEffect, useMemo, useState } from 'react';
import { KnowledgePageKey } from '@kizen/api/knowledge';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  CommonPaginationConfig,
  usePaginationSettings,
} from 'hooks/usePaginationSettings';
import { employeePageConfigQuery } from 'query/employee';
import KnowledgeService from 'services/KnowledgeService';
import { useDebounce } from 'react-use';

type PageConfigOptions = {
  pageKey: KnowledgePageKey;
  defaultConfig: CommonPaginationConfig;
};

export const usePageConfig = ({
  pageKey,
  defaultConfig,
}: PageConfigOptions) => {
  const [ready, setReady] = useState(false);

  const pageConfigQueryOptions = useMemo(
    () =>
      employeePageConfigQuery(pageKey, {
        staleTime: 0,
        refetchOnMount: 'always',
        refetchOnWindowFocus: false,
      }),
    [pageKey]
  );

  const {
    data: pageConfigData,
    isLoading: isLoadingPageConfig,
    isFetching: isFetchingPageConfig,
  } = useQuery(pageConfigQueryOptions);

  const pageIsReady = ready && !isLoadingPageConfig && !isFetchingPageConfig;

  const {
    actualSearch,
    handleChangePage,
    handleChangePageSize,
    handleChangeSearch,
    headData,
    setPaginationSettings,
    paginationSettings,
  } = usePaginationSettings(defaultConfig);

  useLayoutEffect(() => {
    if (pageConfigData) {
      setPaginationSettings((prev) => ({
        ...prev,
        pageSize: pageConfigData.config?.page_size ?? defaultConfig.pageSize,
        ordering: pageConfigData.config?.ordering ?? defaultConfig.ordering,
        search: pageConfigData.config?.search ?? '',
        filters: pageConfigData.config?.filters ?? defaultConfig.filters,
      }));
      setReady(true);
    }
  }, [pageConfigData, setPaginationSettings, defaultConfig]);

  const { mutate: updatePageConfig } = useMutation({
    mutationFn: ({
      pageSize,
      ordering,
      search,
      filters,
    }: CommonPaginationConfig) =>
      KnowledgeService.putKnowledgePageConfig(pageKey, {
        config: {
          page_size: pageSize,
          ordering,
          search,
          // exclude source filter for now as we do not have proper way to handle deleted sources yet
          filters: filters ? { ...filters, source: null } : undefined,
        },
      }),
  });

  useDebounce(
    () => {
      if (pageIsReady) {
        updatePageConfig(paginationSettings);
      }
    },
    300,
    [paginationSettings]
  );

  return {
    actualSearch,
    handleChangePage,
    handleChangePageSize,
    handleChangeSearch,
    headData,
    paginationSettings,
    setPaginationSettings,
    pageIsReady,
  };
};
